import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import heic2any from 'heic2any';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

export default function EventPhotos({ event, currentUserId }) {
  const { token } = useAuth();
  const [photos, setPhotos]       = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState('');
  const fileInputRef              = useRef(null);

  useEffect(() => { fetchPhotos(); }, [event.id]);

  async function fetchPhotos() {
    try {
      const res = await fetch(`${API}/photos?event_id=${event.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (Array.isArray(data)) setPhotos(data);
    } catch(e) { console.error(e); }
  }

 async function compressImage(file) {
  let workingFile = file;

  const isHeic = file.type === 'image/heic' || file.type === 'image/heif' ||
    file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');

  if (isHeic) {
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
    workingFile = new File(
      [converted],
      file.name.replace(/\.(heic|heif)$/i, '.jpg'),
      { type: 'image/jpeg' }
    );
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(workingFile);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX = 1200;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
        else                { width = Math.round(width * MAX / height);  height = MAX; }
      }
      const canvas = document.createElement('canvas');
      canvas.width  = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        blob => {
          if (!blob) return reject(new Error('Compression failed'));
          resolve(new File([blob], workingFile.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }));
        },
        'image/jpeg',
        0.82
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
    img.src = url;
  });
}

async function handleUpload(e) {
  const file = e.target.files[0];
  if (!file) return;

  if (file.size > 5 * 1024 * 1024) {
    setError('Photo must be under 5MB');
    return;
  }

  setUploading(true); setError('');
  try {
    const compressed = await compressImage(file);

    const formData = new FormData();
    formData.append('photo', compressed);
    formData.append('event_id', event.id);

    const res = await fetch(`${API}/photos`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    setPhotos(prev => [...prev, data]);
  } catch(e) {
    setError(e.message);
  } finally {
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }
}

  async function handleDelete(photoId) {
    try {
      const res = await fetch(`${API}/photos/${photoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete photo');
      setPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch(e) {
      setError(e.message);
    }
  }

  const canUpload = photos.length < 9;

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <p style={{ fontSize:13, color:'#7A6555' }}>
          {photos.length === 0
            ? 'No photos yet — be the first to add one!'
            : `${photos.length} of 9 photos`}
        </p>
        {canUpload && (
          <>
            <button
              style={{
                background: '#C4612A', color:'white', border:'none',
                borderRadius:8, padding:'8px 16px', fontFamily:'Jost,sans-serif',
                fontSize:13, fontWeight:500, cursor: uploading ? 'not-allowed' : 'pointer',
                opacity: uploading ? 0.6 : 1,
              }}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}>
              {uploading ? 'Uploading…' : '📷 Add a photo'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/heic,image/heif"
              style={{ display:'none' }}
              onChange={handleUpload}
            />
          </>
        )}
        {!canUpload && (
          <span style={{ fontSize:12, color:'#7A6555', fontStyle:'italic' }}>
            Maximum 9 photos reached
          </span>
        )}
      </div>

      {error && (
        <div style={{ background:'#FFEBEE', color:'#B71C1C', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:16 }}>
          {error}
        </div>
      )}

      {/* Collage */}
      {photos.length > 0 && (
        <div style={{ position:'relative', minHeight:320 }}>
          {photos.map((photo, i) => {
            const rotation  = ROTATIONS[i % ROTATIONS.length];
            const sizeStyle = SIZES[i % SIZES.length];
            const pos       = POSITIONS[i % POSITIONS.length];
            return (
              <div
                key={photo.id}
                style={{
                  position:    'absolute',
                  ...pos,
                  ...sizeStyle,
                  transform:   `rotate(${rotation}deg)`,
                  transition:  'transform .2s',
                  zIndex:      i + 1,
                  background:  'white',
                  padding:     8,
                  borderRadius:4,
                  boxShadow:   '0 2px 12px rgba(44,26,14,.15)',
                  cursor:      'default',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.zIndex = 20;
                  e.currentTarget.style.transform = `rotate(${rotation}deg) scale(1.04)`;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.zIndex = i + 1;
                  e.currentTarget.style.transform = `rotate(${rotation}deg)`;
                }}>
                <img
                  src={photo.url}
                  alt={`Photo by ${photo.profiles?.full_name}`}
                  style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:2, display:'block' }}
                />
                {photo.uploaded_by === currentUserId && (
                  <button
                    onClick={() => handleDelete(photo.id)}
                    style={{
                      position:'absolute', top:4, right:4,
                      background:'rgba(183,28,28,.85)', color:'white',
                      border:'none', borderRadius:'50%', width:22, height:22,
                      fontSize:12, cursor:'pointer', display:'flex',
                      alignItems:'center', justifyContent:'center',
                      lineHeight:1,
                    }}>
                    ×
                  </button>
                )}
                <p style={{ fontSize:10, color:'#7A6555', marginTop:4, textAlign:'center', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                  {photo.profiles?.full_name}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {photos.length === 0 && (
        <div style={{ textAlign:'center', padding:'40px 20px', background:'#FAF7F2', borderRadius:12, border:'1px dashed #E5D9C8' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📷</div>
          <p style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, marginBottom:6 }}>No photos yet</p>
          <p style={{ color:'#7A6555', fontSize:13 }}>Add up to 9 photos from the evening</p>
        </div>
      )}
    </div>
  );
}

// Rotation values in degrees — alternating left/right tilts
const ROTATIONS = [-3, 2, -1.5, 3.5, -2.5, 1, -4, 2.5, -1];

// Varied sizes for the polaroid frames
const SIZES = [
  { width:180, height:180 },
  { width:150, height:160 },
  { width:200, height:170 },
  { width:160, height:190 },
  { width:170, height:155 },
  { width:190, height:180 },
  { width:155, height:165 },
  { width:175, height:175 },
  { width:165, height:185 },
];

// Positions — scattered naturally across the container
const POSITIONS = [
  { top:10,  left:10  },
  { top:20,  left:200 },
  { top:15,  left:390 },
  { top:200, left:60  },
  { top:190, left:260 },
  { top:210, left:450 },
  { top:380, left:20  },
  { top:370, left:220 },
  { top:390, left:420 },
];